/* eslint-disable xstate/invoke-usage */
import { assign, createMachine, InvokeConfig, TransitionsConfig } from 'xstate';
import {
  getLegacyFields,
  hasFields,
  hasInstructionsLegacyField,
  hasMultiSelectImageField,
  hasSingleSelectLegacyField,
  hasTemplateField,
  hasTemplateFieldOfType,
  isCheckboxFields,
  Templates,
} from '../../components/Templates/templates';
import { parseSupplementalInformation } from '../../income-check-v2/machines/helper';
import {
  credentialsService as credentialsServiceImport,
  SUPPLEMENT_CREDENTIALS_FAILED,
} from '../services/credentialsService';
import { Credentials, CredentialsEntity } from '../../models/credentials';
import {
  CreateSupplementalInformationMachine,
  SupplementalInformationMachineContext,
  SupplementalInformationMachineEvents,
  SupplementalInformationMachineServices,
} from '../../income-check-v2/machines/types/supplemental';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { FETCHING_CREDENTIALS_FAILED } from '../../utilities/error';
import { createCredentialsPollerMachine as createCredentialsPollerMachineImport } from './credentialsPollerMachine';

const getPollingInvokeConfig = (): InvokeConfig<
  SupplementalInformationMachineContext,
  SupplementalInformationMachineEvents
> => ({
  id: 'poll-credentials',
  src: 'pollCredentials',
  onDone: {
    target: 'decisionAfterPolling',
    actions: 'persistCredentials',
  },
  onError: [
    {
      target: 'uiError',
      actions: 'actionGetCredentialsError',
    },
  ],
});

const supplementCredentialsTransitionsConfig: TransitionsConfig<
  SupplementalInformationMachineContext,
  SupplementalInformationMachineEvents
> = {
  EVENT_SUPPLEMENT_CREDENTIALS: {
    target: 'processSubmitData',
    actions: 'persistInputFields',
  },
  EVENT_GO_BACK: {
    target: 'processResetSupplementalInformation',
  },
};

export const createSupplementalInformationMachine = ({
  initialContext,
  initialState = 'decisionTemplateNode',
  credentialsService = credentialsServiceImport,
  createCredentialsPollerMachine = createCredentialsPollerMachineImport,
}: CreateSupplementalInformationMachine) =>
  createMachine(
    {
      predictableActionArguments: true,
      schema: {
        context: {} as SupplementalInformationMachineContext,
        events: {} as SupplementalInformationMachineEvents,
        services: {} as SupplementalInformationMachineServices,
      },
      tsTypes: {} as import('./supplementalInformationMachine.typegen').Typegen0,
      id: 'supplemental-information-machine',
      context: (): SupplementalInformationMachineContext => {
        const parsedSupplementalInformation = parseSupplementalInformation(
          initialContext.credentials.supplementalInformation
        );

        return {
          inputFields: null,
          credentials: initialContext.credentials,
          selectedProvider: initialContext.selectedProvider,
          supplementalInformation: parsedSupplementalInformation,
          legacyFields: getLegacyFields(parsedSupplementalInformation),
        };
      },
      initial: initialState,
      states: {
        decisionTemplateNode: {
          always: [
            {
              target: 'uiPollingAppCodeTemplate',
              cond: 'isAppCodeTemplate',
            },
            {
              target: 'uiPollingCardReaderTemplate',
              cond: 'isCardReaderTemplate',
            },
            {
              target: 'uiPollingChipTanTemplate',
              cond: 'isChipTanTemplate',
            },
            {
              target: 'uiPollingSmsCodeTemplate',
              cond: 'isSmsCodeTemplate',
            },
            {
              target: 'uiPollingQrCodeTemplate',
              cond: 'isQrCodeTemplate',
            },
            {
              target: 'uiPollingDecoupledTemplate',
              cond: 'isDecoupledTemplate',
            },
            {
              target: 'uiPollingOtpMethodsTemplate',
              cond: 'isOtpMethodsTemplate',
            },
            {
              target: 'uiPollingVerificationTemplate',
              cond: 'isVerificationTemplate',
            },
            {
              target: 'uiPollingImageQuestionTextAnswerTemplate',
              cond: 'isImageTemplate',
            },
            {
              target: 'uiPollingSingleSelectImageTemplate',
              cond: 'isSingleSelectImageFieldTemplate',
            },
            {
              target: 'uiPollingMultiSelectImageTemplate',
              cond: 'isMultiSelectImageFieldTemplate',
            },
            {
              target: 'uiErrorUnknownTemplate',
              cond: 'isUnknownTemplate',
            },
            {
              target: 'uiPollingSingleSelectTemplate',
              cond: 'isSingleSelectLegacyFieldTemplate',
            },
            {
              target: 'uiPollingInstructionTemplate',
              cond: 'isInstructionsLegacyFieldTemplate',
            },
            {
              target: 'uiPollingCheckboxTemplate',
              cond: 'isCheckboxTemplate',
            },
            {
              target: 'uiPollingMultipleFieldsTemplate',
              cond: 'isMultipleFieldsLegacyTemplate',
            },
            {
              target: 'uiDone',
            },
          ],
        },
        uiPollingAppCodeTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingCardReaderTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingChipTanTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingSmsCodeTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingSingleSelectTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingSingleSelectImageTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingMultiSelectImageTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingInstructionTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingMultipleFieldsTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingImageQuestionTextAnswerTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingCheckboxTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingOtpMethodsTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingVerificationTemplate: {
          invoke: getPollingInvokeConfig(),
          on: supplementCredentialsTransitionsConfig,
        },
        uiPollingDecoupledTemplate: {
          invoke: getPollingInvokeConfig(),
          on: {
            EVENT_SUPPLEMENT_CREDENTIALS: [
              {
                target: 'processSubmitData',
                actions: 'persistInputFields',
              },
            ],
            EVENT_AUTO_SUPPLEMENT_CREDENTIALS: [
              {
                target: 'processSubmitDecoupledData',
                actions: 'persistInputFields',
              },
            ],
          },
        },
        uiPollingQrCodeTemplate: {
          invoke: getPollingInvokeConfig(),
          on: {
            EVENT_SUPPLEMENT_CREDENTIALS: [
              {
                target: 'processSubmitData',
                actions: 'persistInputFields',
              },
            ],
            EVENT_AUTO_SUPPLEMENT_CREDENTIALS: [
              {
                target: 'processSubmitQrCodeData',
                actions: 'persistInputFields',
              },
            ],
          },
        },
        decisionAfterPolling: {
          always: [
            {
              target: 'decisionTemplateNode',
              cond: 'isAwaitingSupplementalInformationCtx',
            },
            {
              target: 'uiError',
              cond: 'hasCredentialsErrorCtx',
            },
            {
              target: 'uiDone',
            },
          ],
        },
        processSubmitDecoupledData: {
          invoke: {
            id: 'auto-supplement-credentials',
            src: 'submitCredentials',
            onDone: [
              {
                target: 'processDecoupledCredentialsPoller',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'actionSupplementCredentialsError',
              },
            ],
          },
        },
        processSubmitQrCodeData: {
          invoke: {
            id: 'auto-supplement-credentials',
            src: 'submitCredentials',
            onDone: [
              {
                target: 'processQrCodeCredentialsPoller',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'actionSupplementCredentialsError',
              },
            ],
          },
        },
        processSubmitData: {
          invoke: {
            id: 'supplement-credentials',
            src: 'submitCredentials',
            onDone: [
              {
                target: 'processCredentialsPoller',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'actionSupplementCredentialsError',
              },
            ],
          },
        },
        processCredentialsPoller: {
          invoke: {
            id: 'poll-credentials',
            src: 'pollCredentials',
            onDone: [
              {
                target: 'uiError',
                cond: 'hasCredentialsError',
                actions: 'persistCredentialsError',
              },
              {
                target: 'decisionTemplateNode',
                cond: 'isAwaitingSupplementalInformation',
                actions: 'persistCredentials',
              },
              {
                target: 'uiDone',
                actions: 'persistCredentialsObject',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'actionGetCredentialsError',
              },
            ],
          },
        },
        processDecoupledCredentialsPoller: {
          invoke: {
            id: 'poll-credentials',
            src: 'pollCredentials',
            onDone: [
              {
                target: 'uiError',
                cond: 'hasCredentialsError',
                actions: 'persistCredentialsError',
              },
              {
                target: 'decisionTemplateNode',
                cond: 'isAwaitingSupplementalInformation',
                actions: 'persistCredentials',
              },
              {
                target: 'uiDone',
                actions: 'persistCredentialsObject',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'actionGetCredentialsError',
              },
            ],
          },
        },
        processQrCodeCredentialsPoller: {
          invoke: {
            id: 'poll-credentials',
            src: 'pollCredentials',
            onDone: [
              {
                target: 'uiError',
                cond: 'hasCredentialsError',
                actions: 'persistCredentialsError',
              },
              {
                target: 'decisionTemplateNode',
                cond: 'isAwaitingSupplementalInformation',
                actions: 'persistCredentials',
              },
              {
                target: 'uiDone',
                actions: 'persistCredentialsObject',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'actionGetCredentialsError',
              },
            ],
          },
        },
        processResetSupplementalInformation: {
          invoke: {
            id: 'reset-supplemental-information',
            src: 'resetSupplementalInformation',
            onDone: {
              target: 'processCredentialsPoller',
            },
            onError: {
              target: 'uiError',
              actions: 'actionSupplementCredentialsError',
            },
          },
        },
        uiErrorUnknownTemplate: {
          type: 'final',
        },
        uiDone: {
          type: 'final',
        },
        uiError: {
          type: 'final',
        },
      },
    },
    {
      guards: {
        isAwaitingSupplementalInformation: (
          _: SupplementalInformationMachineContext,
          event: { data: CredentialsEntity }
        ) => Credentials.isAwaitingSupplementalInformation(event.data),
        isAwaitingSupplementalInformationCtx: (ctx) =>
          Credentials.isAwaitingSupplementalInformation(ctx.credentials),
        hasCredentialsError: (
          _: SupplementalInformationMachineContext,
          event: { data: CredentialsEntity }
        ) => Credentials.hasError(event.data),
        hasCredentialsErrorCtx: ({ credentials }) => Credentials.hasError(credentials),
        isAppCodeTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.APP_CODE),
        isCardReaderTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.CARD_READER),
        isDecoupledTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.DECOUPLED),
        isSmsCodeTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.SMS_CODE),
        isChipTanTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.CHIP_TAN),
        isQrCodeTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.QR_CODE),
        isOtpMethodsTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.OTP_VERIFICATION_METHODS),
        isVerificationTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.VERIFICATION),
        isImageTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.IMAGE_CODE),
        isSingleSelectImageFieldTemplate: ({ supplementalInformation }) =>
          hasTemplateFieldOfType(supplementalInformation, Templates.IMAGE_SELECTION),
        isUnknownTemplate: ({ supplementalInformation }) =>
          hasTemplateField(supplementalInformation),
        isSingleSelectLegacyFieldTemplate: ({ legacyFields }) =>
          hasSingleSelectLegacyField(legacyFields),
        isMultiSelectImageFieldTemplate: ({ supplementalInformation }) =>
          /* Not yet in use, will be used for Image Captcha like MFAs */
          hasMultiSelectImageField(supplementalInformation),
        isInstructionsLegacyFieldTemplate: ({ legacyFields }) =>
          hasInstructionsLegacyField(legacyFields),
        isMultipleFieldsLegacyTemplate: ({ legacyFields }) => hasFields(legacyFields),
        isCheckboxTemplate: ({ legacyFields }) => {
          return hasFields(legacyFields) && isCheckboxFields(legacyFields);
        },
      },
      services: {
        pollCredentials: ({ credentials }) => createCredentialsPollerMachine({ credentials }),
        submitCredentials: ({ credentials, inputFields }) =>
          credentialsService.supplement(credentials.id, inputFields),
        resetSupplementalInformation: ({ credentials }) =>
          credentialsService.supplement(credentials.id, null),
      },
      actions: {
        persistCredentials: assign({
          credentials: (_, event) => event.data,
          supplementalInformation: (_, event) =>
            parseSupplementalInformation(event.data?.supplementalInformation),
          legacyFields: (_, event) =>
            getLegacyFields(parseSupplementalInformation(event.data?.supplementalInformation)),
        }),
        persistCredentialsObject: assign({
          credentials: (_, event) => event.data,
        }),
        persistCredentialsError: assign({
          credentials: (_, event) => event.data,
        }),
        persistInputFields: assign({
          inputFields: (_, event) => event.inputFields,
        }),
        actionGetCredentialsError: assign({
          error: (_) => new ApplicationError(FETCHING_CREDENTIALS_FAILED),
        }),
        actionSupplementCredentialsError: assign({
          error: (_) => new ApplicationError(SUPPLEMENT_CREDENTIALS_FAILED),
        }),
      },
    }
  );
